// Theme state. The actual colours live in src/theme.css as custom properties;
// this only decides which class sits on <html>.
//
// Three settings, not two: "system" follows the OS, which is what a phone user
// expects when their device flips to dark at sunset. An explicit choice pins it.
//
// The FIRST application happens in index.html, in a blocking inline script
// before any CSS paints — otherwise a teacher on light mode gets a black flash
// on every cold load. This module must therefore agree exactly with that
// script; keep THEME_KEY and the resolve logic in sync with it.

import { resolveToken } from "../src/themeColors";

export type ThemeSetting = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

export const THEME_KEY = "app_theme";

export function getThemeSetting(): ThemeSetting {
  const v = localStorage.getItem(THEME_KEY);
  return v === "light" || v === "dark" ? v : "system";
}

export function systemTheme(): ResolvedTheme {
  return window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

export function resolveTheme(setting: ThemeSetting = getThemeSetting()): ResolvedTheme {
  return setting === "system" ? systemTheme() : setting;
}

// Swaps the class on <html> and keeps the browser chrome (iOS status bar,
// Android address bar) in step — a light page under a near-black status bar
// looks broken on a phone.
export function applyTheme(setting: ThemeSetting): ResolvedTheme {
  const resolved = resolveTheme(setting);
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(resolved);
  const meta = document.querySelector('meta[name="theme-color"]');
  // Read the page background straight from the token so the browser chrome
  // always matches src/theme.css.
  if (meta) meta.setAttribute("content", resolveToken("--app-surface"));
  return resolved;
}

export function setThemeSetting(setting: ThemeSetting): ResolvedTheme {
  if (setting === "system") localStorage.removeItem(THEME_KEY);
  else localStorage.setItem(THEME_KEY, setting);
  return applyTheme(setting);
}

// Fires when the OS theme changes, but only matters while the setting is
// "system" — an explicit choice must not be overridden at sunset. Returns an
// unsubscribe function.
export function watchSystemTheme(onChange: (t: ResolvedTheme) => void): () => void {
  const mq = window.matchMedia?.("(prefers-color-scheme: light)");
  if (!mq) return () => {};
  const handler = () => {
    if (getThemeSetting() !== "system") return;
    onChange(applyTheme("system"));
  };
  mq.addEventListener("change", handler);
  return () => mq.removeEventListener("change", handler);
}
